// @flow

import 'isomorphic-fetch'
import { push } from 'react-router-redux'
import {
  checkHttpStatus,
  getBaseUrl,
  getApiHeaders,
  getApiBaseUrlWithOrg
} from '../utils'
import { setCurrentOrganization } from './organization'
import { userNeedsToAcceptPolicies, setPoliciesToAccept } from './policies'
import { fetchOrganizations } from './organizations'
import { fetchDataAndRedirectUser } from './combined'
import {
  getErrorAction,
  removeNonQuestMakerRoleOrganizations,
  handlePotentialDefaultOrgRoleIssue,
  doesUserHaveAppAccess
} from '../utils/actions'

import { basicGet } from '../utils/api'
import type { Action } from '../types/actions/user'
import type { APIError } from '../types'

export const LOGIN_USER_REQUEST = 'LOGIN_USER_REQUEST'
export const LOGIN_USER_SUCCESS = 'LOGIN_USER_SUCCESS'
export const LOGIN_USER_FAILURE = 'LOGIN_USER_FAILURE'
export const LOGOUT_USER = 'LOGOUT_USER'
export const USER_DATA_SUCCESS = 'USER_DATA_SUCCESS'
export const USER_DATA_FAILURE = 'USER_DATA_FAILURE'
export const FORGOT_PASSWORD_REQUEST = 'FORGOT_PASSWORD_REQUEST'
export const FORGOT_PASSWORD_SUCCESS = 'FORGOT_PASSWORD_SUCCESS'
export const FORGOT_PASSWORD_FAILURE = 'FORGOT_PASSWORD_FAILURE'
export const RESET_PASSWORD_REQUEST = 'RESET_PASSWORD_REQUEST'
export const RESET_PASSWORD_SUCCESS = 'RESET_PASSWORD_SUCCESS'
export const RESET_PASSWORD_FAILURE = 'RESET_PASSWORD_FAILURE'
export const GET_USER_DATA_REQUEST = 'GET_USER_DATA_REQUEST'
export const GET_USER_DATA_SUCCESS = 'GET_USER_DATA_SUCCESS'
export const GET_USER_DATA_FAILURE = 'GET_USER_DATA_FAILURE'
export const USER_IS_LOGGED_IN_FETCHING_ALL_DATA =
  'USER_IS_LOGGED_IN_FETCHING_ALL_DATA'
export const USER_IS_LOGGED_IN_DATA_FETCHED = 'USER_IS_LOGGED_IN_DATA_FETCHED'
export const USER_NEEDS_TO_RESET_PASSWORD = 'USER_NEEDS_TO_RESET_PASSWORD'

export function userNeedsToResetPassword(reset) {
  return {
    type: USER_NEEDS_TO_RESET_PASSWORD,
    userNeedsToResetPassword: reset
  }
}

export function loginUserSuccess(response: Object): Action {
  sessionStorage.setItem('oef-token', response.token)
  return {
    type: LOGIN_USER_SUCCESS,
    payload: {
      token: response.token,
      name: response.name,
      user: response
    }
  }
}

export function loginUserFailure(error: APIError): Action {
  return getErrorAction(LOGIN_USER_FAILURE, error)
}

export function loginUserRequest(): Action {
  return {
    type: LOGIN_USER_REQUEST
  }
}

export function logout(): Action {
  sessionStorage.clear()
  return {
    type: LOGOUT_USER
  }
}

export function userDataSuccess(response: Object): Action {
  return {
    type: USER_DATA_SUCCESS,
    questCounts: response.quest_counts
  }
}

export function userDataFailure(error: APIError): Action {
  return getErrorAction(USER_DATA_FAILURE, error)
}

export function getUserDataRequest(): Action {
  return {
    type: GET_USER_DATA_REQUEST
  }
}

export function getUserDataSuccess(response): Action {
  return {
    type: GET_USER_DATA_SUCCESS,
    payload: {
      stats: response
    }
  }
}

export function getUserDataFailure(error): Action {
  return getErrorAction(GET_USER_DATA_FAILURE, error)
}

export function userIsLoggedInFetchingAllData(): Object {
  return {
    type: USER_IS_LOGGED_IN_FETCHING_ALL_DATA
  }
}

export function userIsLoggedInDataFetched(): Object {
  return {
    type: USER_IS_LOGGED_IN_DATA_FETCHED
  }
}

export function requestUserData(): Function {
  return async (dispatch, getState) => {
    const state = getState()
    const url = `${getBaseUrl()}/quest_maker/api/user`
    try {
      const response = checkHttpStatus(
        await fetch(url, {
          method: 'GET',
          headers: getApiHeaders(state)
        })
      )
      const json = await response.json()
      dispatch(userDataSuccess(json))
    } catch (error) {
      dispatch(userDataFailure(error))
    }
  }
}

export function loginUser(email: string, password: string): Function {
  return async (dispatch, getState) => {
    const url = `${getBaseUrl()}/login`
    let data = new window.FormData()
    data.append('session[email]', email)
    data.append('session[password]', password)
    data.append('session[application]', 'QM')
    dispatch(loginUserRequest())

    // requirement to store last attempted login value
    localStorage.setItem('lastLogin', email)
    sessionStorage.setItem('password', password)
    try {
      const response = checkHttpStatus(
        await fetch(url, {
          method: 'POST',
          body: data
        })
      )

      let user = await response.json()
      user = removeNonQuestMakerRoleOrganizations(user)

      if (doesUserHaveAppAccess(user)) {
        user = handlePotentialDefaultOrgRoleIssue(user)
        dispatch(handleLoginSuccess(user))
      } else {
        dispatch(
          loginUserFailure({
            response: {
              status: 401,
              statusText: 'There was an error logging in.'
            }
          })
        )
      }
    } catch (error) {
      dispatch(loginUserFailure(error))
    }
  }
}

export function logoutAndRedirect(
  reload: ?boolean,
  redirect: ?boolean
): Function {
  return dispatch => {
    // any time we logout, remove session password in case it exists
    sessionStorage.removeItem('password')
    dispatch(logout())

    if (reload) {
      window.location.reload(true)
    } else {
      dispatch(
        push(
          redirect ? `/login?redirect=${window.location.pathname}` : '/login'
        )
      )
    }
  }
}

export function redirectAuthenticatedUser(): Function {
  return async (dispatch, getState) => {
    const state = getState()
    let route
    if (state.user.user.organization_roles.some(role => role === 'qm_mentor_author')) {
      route = '/mycourses'
    } else {
      route = '/quests'
    }
    return dispatch(push(route))
  }
}

export function getUserData(): Function {
  return async (dispatch, getState) => {
    const state = getState()
    const url = `${getApiBaseUrlWithOrg(state.organization.orgId)}/user`
    return basicGet(
      state,
      dispatch,
      url,
      getUserDataRequest,
      getUserDataSuccess,
      getUserDataFailure
    )
  }
}

export function handleLoginSuccess(user) {
  return async dispatch => {
    await dispatch(loginUserSuccess(user))
    await dispatch(fetchOrganizations())
    await dispatch(
      setCurrentOrganization(user.org_id, user.organization_roles, false)
    )

    dispatch(userIsLoggedInFetchingAllData())
    const policiesToAccept = await userNeedsToAcceptPolicies(user.token)

    if (policiesToAccept) {
      dispatch(setPoliciesToAccept(policiesToAccept))
    } else {
      dispatch(fetchDataAndRedirectUser(user))
    }
  }
}



// WEBPACK FOOTER //
// ./src/actions/user.js